import React, { useEffect, useState } from "react";
import { metrics } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Sprout, Flame, Activity } from "lucide-react";

function Slider({ label, value, onChange, testid, hint }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span className="text-[#6B6B6B]">{label}</span>
        <span className="font-medium text-[#4F6F52]">{value}/10</span>
      </div>
      <input
        data-testid={testid}
        type="range"
        min={1}
        max={10}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-[#4F6F52]"
      />
      {hint && <p className="text-xs text-[#6B6B6B] mt-1.5 italic">{hint}</p>}
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!value)}
      className={`w-full rounded-2xl border px-4 py-3.5 flex items-center justify-between transition ${
        value
          ? "bg-[#E1ECDD] border-[#4F6F52]/40 text-[#4F6F52]"
          : "bg-white border-[#DCE5DB] text-[#6B6B6B] hover:border-[#4F6F52]/30"
      }`}
    >
      <span className="font-medium text-sm">{label}</span>
      <span className={`w-5 h-5 rounded-full border transition ${value ? "bg-[#4F6F52] border-[#4F6F52]" : "border-[#cdd5c9]"}`} />
    </button>
  );
}

function ScoreRing({ score }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, score)) / 100) * c;
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" className="block">
      <circle cx="48" cy="48" r={r} stroke="#DCE5DB" strokeWidth="8" fill="none" />
      <circle
        cx="48"
        cy="48"
        r={r}
        stroke="#4F6F52"
        strokeWidth="8"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 48 48)"
        style={{ transition: "stroke-dashoffset 800ms ease" }}
      />
      <text
        x="48"
        y="54"
        textAnchor="middle"
        fontFamily="Poppins, Inter, sans-serif"
        fontSize="22"
        fontWeight="600"
        fill="#2E2E2E"
      >
        {score}
      </text>
    </svg>
  );
}

export default function GrowthPage() {
  const { user, refresh } = useAuth();
  const [check, setCheck] = useState(true);
  const [prayer, setPrayer] = useState(false);
  const [scriptureRead, setScriptureRead] = useState(false);
  const [emo, setEmo] = useState(7);
  const [com, setCom] = useState(7);
  const [note, setNote] = useState("");
  const [week, setWeek] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    metrics.week().then(setWeek).catch(() => {});
    metrics
      .today()
      .then((t) => {
        if (t) {
          setCheck(t.check_in);
          setPrayer(t.prayer);
          setScriptureRead(t.scripture_read);
          setEmo(t.emotional_control);
          setCom(t.communication);
          setNote(t.note || "");
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await metrics.upsert({
        check_in: check,
        prayer,
        scripture_read: scriptureRead,
        emotional_control: emo,
        communication: com,
        note,
      });
      toast.success("Today’s reflection saved");
      load();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Let’s try that again");
    } finally {
      setBusy(false);
    }
  };

  const days = week?.days || [];
  const score = week?.spiritual_health_score ?? 0;
  const streak = week?.streak ?? user?.streak ?? 0;

  const encouragement = score >= 80
    ? "You’re showing up beautifully."
    : score >= 50
    ? "Steady steps — keep going."
    : score > 0
    ? "Every small moment counts."
    : "A new chapter starts today.";

  return (
    <div className="space-y-8">
      <div className="fade-in-up">
        <h1 className="font-display text-3xl text-[#2E2E2E]">Your progress this week</h1>
        <p className="text-[#6B6B6B] text-sm mt-1.5 leading-relaxed">
          Small, faithful steps. No judgment — just reflection.
        </p>
      </div>

      {/* Score ring + streak */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 fade-in-up stagger-1">
        <div data-testid="stat-health" className="rounded-3xl border border-[#DCE5DB] bg-white p-6 flex items-center gap-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
          <ScoreRing score={score} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B6B6B] flex items-center gap-1.5">
              <Sprout size={12} /> Wellbeing
            </div>
            <div className="font-display text-xl text-[#2E2E2E] mt-0.5">{encouragement}</div>
            <div className="text-xs text-[#6B6B6B] mt-1">A gentle measure of your week.</div>
          </div>
        </div>
        <div data-testid="stat-streak" className="rounded-3xl border border-[#DCE5DB] bg-[#F5EBD9]/60 p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 text-[#8C7A4F]">
            <Flame size={14} />
            <div className="text-xs uppercase tracking-[0.14em]">Showing up</div>
          </div>
          <div className="font-display text-4xl text-[#2E2E2E] mt-2">
            {streak} <span className="text-base text-[#6B6B6B] font-normal ml-1">{streak === 1 ? "day" : "days"} in a row</span>
          </div>
          <div className="text-xs text-[#6B6B6B] mt-2 leading-relaxed">
            {streak >= 7 ? "A faithful rhythm." : streak >= 3 ? "It’s starting to feel natural." : "One quiet moment at a time."}
          </div>
        </div>
      </div>

      {/* Weekly bars */}
      <div data-testid="weekly-summary" className="rounded-3xl border border-[#DCE5DB] bg-white p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-2">
        <div className="flex items-center gap-2 mb-5">
          <Activity size={15} className="text-[#4F6F52]" />
          <h2 className="font-display text-lg">A look at your week</h2>
        </div>
        {days.length === 0 ? (
          <p className="text-sm text-[#6B6B6B]">No reflections yet — start with today below.</p>
        ) : (
          <div className="grid grid-cols-7 gap-2 items-end h-32">
            {days.map((d) => {
              const v = (d.emotional_control + d.communication) / 2;
              const h = Math.max(8, (v / 10) * 100);
              return (
                <div key={d.date} className="flex flex-col items-center gap-1.5">
                  <div
                    className="w-full rounded-xl bg-gradient-to-t from-[#7FA7B0] to-[#4F6F52]"
                    style={{ height: `${h}%` }}
                    title={`${d.date}: ${v.toFixed(1)}/10`}
                  />
                  <div className="text-[10px] text-[#6B6B6B]">
                    {new Date(d.date).toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {week && (
          <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
            <Mini label="Steady emotions" value={`${week.avg_emotional_control}/10`} />
            <Mini label="Kind communication" value={`${week.avg_communication}/10`} />
            <Mini label="Days in prayer" value={`${week.prayer_count}/7`} />
            <Mini label="Days with scripture" value={`${week.scripture_count}/7`} />
          </div>
        )}
      </div>

      {/* Today's check-in */}
      <div data-testid="checkin-form" className="rounded-3xl border border-[#DCE5DB] bg-[#EEF3EC] p-6 space-y-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-3">
        <div>
          <h2 className="font-display text-xl">A moment of honesty</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">Just for today. No pressure to be perfect.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Toggle label="I prayed today" value={prayer} onChange={setPrayer} testid="toggle-prayer" />
          <Toggle label="I read scripture" value={scriptureRead} onChange={setScriptureRead} testid="toggle-scripture" />
        </div>
        <Slider label="How steady did you feel?" value={emo} onChange={setEmo} testid="slider-emo" hint="Emotions, reactions, patience." />
        <Slider label="How were your words to others?" value={com} onChange={setCom} testid="slider-com" hint="Tone, kindness, listening." />
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-1.5">Anything you’d like to remember? (optional)</label>
          <textarea
            data-testid="checkin-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
            placeholder="A small thing you’re grateful for, or wrestled with..."
          />
        </div>
        <div className="flex justify-end">
          <button
            data-testid="checkin-save"
            onClick={save}
            disabled={busy}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 text-sm font-medium transition disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
          >
            {busy ? "Saving..." : "Save today’s reflection"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="flex justify-between bg-white rounded-2xl px-4 py-2.5 border border-[#DCE5DB]">
      <span className="text-[#6B6B6B]">{label}</span>
      <span className="font-medium text-[#4F6F52]">{value}</span>
    </div>
  );
}
